import * as Yup from 'yup';

import {
  aopDeductibleIsAlwaysPercent,
  noWindHailDeductibleStates,
  windHailDeductibleCanVaryByCounty
} from '@ourbranch/lookups';
import { isBefore, isAfter, subMonths } from 'date-fns';

import { validHomePurchaseDate } from 'common/helpers/yup-helpers';
import { currencyFormatter } from 'core/helpers/formatters';

Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);

const schema = (state, session) => {
  return Yup.object().shape({
    home: Yup.object().shape({
      purchaseDate: Yup.string().validHomePurchaseDate('home.purchaseDate', false),
      roofYear: Yup.number()
        .positive()
        .integer()
        .test('roofYearMin', 'Roof year must be higher than 1899', (value) => value >= 1899)
        .test(
          'roofYearMax',
          'Roof year cannot be greater than current year',
          (value) => value <= new Date().getUTCFullYear()
        )
        .test('is-older-than-year-built', 'Roof year cannot be older than home built year', function (value) {
          const { yearBuilt } = this.parent;
          if (!yearBuilt) {
            return true;
          }
          return value >= yearBuilt;
        })
        .test(
          'roofYearMaxOnData',
          'Based on imagery and/or permit data, we will need additional evidence to allow a roof year this new.',
          function (value) {
            // Cape Analytics provides roofReplacementYearMax based on imagery analysis
            // If present, roof year cannot exceed this external data constraint
            const { roofReplacementYearMax } = this.parent;
            // Allow override for staff with appropriate permissions; using both names because it looks like it
            // could be defined either way
            const hasOverridePermission =
              session?.canEditUneditablePropertyDetails || session?.canEditUneditablePropertyDetailsFields;
            // also allow override if the overrides flag is set:
            const overridesEnabled = this.options.context?.overrides?.canEditUneditablePropertyDetails;
            return (
              !roofReplacementYearMax || value <= roofReplacementYearMax || hasOverridePermission || overridesEnabled
            );
          }
        ),
      sqFt: Yup.number()
        .positive()
        .integer()
        .test('sqFt-must-be-greater-than-0', 'sqFt must be a positive number', (val) => {
          return Number(val) && val > 0;
        })
    }),
    homeCoverage: Yup.object().shape({
      coverageA: Yup.number().test(
        'between-100-and-120-percent-rce',
        'RCE must be between 100% and 120%',
        function (value) {
          const rce = this.options.context?.home.replacementCostEstimate.total;
          const rceFormatted = currencyFormatter(rce, { removeDecimal: true });

          const max = rce * 1.2;
          const maxFormatted = currencyFormatter(max, { removeDecimal: true });

          if (value < rce || value > max) {
            return this.createError({
              message: `Coverage adjustment is out of range. You may adjust this from ${rceFormatted} (100% RCE) to ${maxFormatted} (120% RCE).`
            });
          }
          return true;
        }
      ),
      deductibleWindHail: Yup.number()
        .nullable()
        .test(
          'CompareToAllOtherPerils',
          'Wind & Hail Deductible must be greater than or equal to all other perils deductible',
          function (value) {
            const windHailExclusion = this.options.context?.homeCoverage?.windHailExclusion;
            if (windHailExclusion) {
              // force pass since no need to validiate this if excluded
              return true;
            }
            const hasHomeSelected = this.options.context?.selectedOption?.includes('H');
            const county = this.options.context?.home?.county?.toUpperCase();
            const windHailInState =
              (!windHailDeductibleCanVaryByCounty[state] ||
                (windHailDeductibleCanVaryByCounty[state] && windHailDeductibleCanVaryByCounty[state][county])) &&
              !noWindHailDeductibleStates[state];

            if (hasHomeSelected && windHailInState) {
              const coverageA = this.options.context.homeCoverage?.coverageA;
              const windHailIsPercent = value < 1;
              const allOtherPerilsIsPercent = this.options.context.homeCoverage?.deductibleAllOther < 1;

              // since value is a %, we need to multiply it by coverage A to see the deductible amount
              const allOtherPerilsAmount = allOtherPerilsIsPercent
                ? this.options.context.homeCoverage?.deductibleAllOther * coverageA
                : this.options.context.homeCoverage?.deductibleAllOther;
              const windHailAmount = windHailIsPercent ? value * coverageA : value;

              return windHailAmount >= allOtherPerilsAmount;
            }
            return true;
          }
        ),
      deductibleAllOther: Yup.number()
        .nullable()
        .test(
          'WindHailPercentageMin',
          'All Other Perils deductible must be a percentage for this state',
          function (value) {
            const hasHomeSelected = this.options.context?.selectedOption?.includes('H');
            if (hasHomeSelected && aopDeductibleIsAlwaysPercent[state] && value > 1) {
              return false;
            }
            return true;
          }
        )
    })
  });
};

export default schema;
